import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import type { AssignmentRow } from '../lib/sitting';
import { Banner } from '../components/Banner';
import { useAuth } from '../store/auth';
import { checkBrowser } from '../exam/browser-check';
import { InstructionVideo } from '../exam/InstructionVideo';

/**
 * Whether this candidate has passed the entry screen today.
 *
 * Session-scoped, so closing the browser puts it back — a candidate returning
 * for a second sitting is checked again, which is the point of checking — but
 * a reload mid-morning does not make them do it twice.
 */
const ENTERED_KEY = 'target.entered';

const hasEntered = () => {
  try {
    return sessionStorage.getItem(ENTERED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Which sections the candidate has watched the instructions for.
 *
 * Session-scoped: watching it once is enough for the day, and a reload does
 * not make somebody sit through it again — but it does not follow them to
 * tomorrow's sitting either.
 */
const CONFIRMED_KEY = 'target.confirmed';

function readConfirmed(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(CONFIRMED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function writeConfirmed(ids: Set<string>) {
  try {
    sessionStorage.setItem(CONFIRMED_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage blocked: the film simply has to be watched again on reload.
  }
}

interface Film {
  url: string;
  mimeType: string | null;
}

/**
 * The order a candidate sits them, which is the order the real test day runs
 * in — not the order a teacher happened to assign them.
 */
const MODULE_ORDER = { LISTENING: 0, READING: 1, WRITING: 2 } as const;

/** What the section is called on the day. */
function sectionName(test: AssignmentRow['test']): string {
  // Listening, Reading, Writing — the names the real screen prints, and the
  // names a candidate uses. Whether the Reading is Academic or General
  // Training is the centre's business when it sets the paper, not something
  // the candidate is asked to read on the morning.
  if (test.module === 'LISTENING') return 'Listening';
  return test.module === 'READING' ? 'Reading' : 'Writing';
}

/**
 * "1 hour", "30 minutes" — the way the timing is written on the real test-day
 * screen, rather than the terse form used in the teacher's console.
 */
function timing(ms: number | null): string {
  if (!ms) return 'Timing: not set';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `Timing: ${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hoursText = `${hours} hour${hours === 1 ? '' : 's'}`;
  return rest === 0
    ? `Timing: ${hoursText}`
    : `Timing: ${hoursText} ${rest} minutes`;
}

/** The heavy check a candidate scans the page for. */
function Tick() {
  return (
    <svg
      className="sitting__check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="Completed"
    >
      <path d="M4 12.5 9.5 18 20 6.5" />
    </svg>
  );
}

/** A finished section, however it ended. */
const isDone = (row: AssignmentRow) =>
  row.session?.state === 'SUBMITTED' || row.session?.state === 'EXPIRED';

/**
 * The screen a candidate meets first, before the day's sections.
 *
 * It exists to say three things in the order the real sitting says them: whose
 * session this is, what they are about to enter, and that the machine in front
 * of them can actually run it. The check is a real one — see checkBrowser —
 * because a tick that always appears teaches a candidate to ignore it.
 */
function CandidateEntry({
  sections,
  finished,
  onEnter,
}: {
  sections: number;
  /** Sections already sat, which are not counted as today's. */
  finished: number;
  onEnter: () => void;
}) {
  const [check] = useState(checkBrowser);

  return (
    <div className="entry">
      <section className="entry__card">
        <div className="entry__title">
          {/* The centre's name and the candidate's are both in the bar above.
              This names what they are about to enter, which is the one thing
              the bar does not say. */}
          <h1>Computer-delivered practice test</h1>
          <p className="muted">
            {sections === 0
              ? finished > 0
                ? 'You have no tests left to sit.'
                : 'No sections are scheduled for you yet.'
              : `${sections} ${sections === 1 ? 'section' : 'sections'} today.`}
          </p>
        </div>

        <div className={`entry__check ${check.ok ? '' : 'entry__check--bad'}`}>
          {check.ok ? (
            <>
              <span className="entry__tick" aria-hidden="true">
                ✓
              </span>
              <span>Your browser has been approved</span>
            </>
          ) : (
            <div className="stack" style={{ gap: 'var(--s-2)' }}>
              <strong>This browser is not ready</strong>
              {check.failures.map((f) => (
                <span key={f}>{f}</span>
              ))}
              <span className="muted">
                You can continue, but the test may not work properly. Tell your teacher.
              </span>
            </div>
          )}
        </div>

        <div className="entry__go">
          <button className="btn btn--dark" onClick={onEnter}>
            Enter
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * The candidate's screen on test day.
 *
 * Modelled on the sitting a candidate will actually face: one card per
 * section, its timing, and a tick once it is behind them — so nothing about
 * this screen is a surprise when they sit the real thing. Practising on a
 * different-looking console teaches the wrong reflexes.
 */
export function StudentHomePage() {
  const signOut = useAuth((s) => s.signOut);
  const navigate = useNavigate();
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entered, setEntered] = useState(hasEntered);
  const [films, setFilms] = useState<Record<string, Film>>({});
  const [confirmed, setConfirmed] = useState<Set<string>>(readConfirmed);
  /** Whether this centre lets its candidates start without watching the film. */
  const [skippable, setSkippable] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ assignments: AssignmentRow[]; centre?: { instructionsSkippable?: boolean } }>(
        '/my/assignments',
      )
      .then(({ data }) => {
        setRows(
          [...data.assignments].sort(
            (a, b) => MODULE_ORDER[a.test.module] - MODULE_ORDER[b.test.module],
          ),
        );
        setSkippable(Boolean(data.centre?.instructionsSkippable));
      })
      .catch((err) => setError(errorMessage(err, 'Could not load your tests')))
      .finally(() => setLoading(false));
  }, []);

  // The instructions for each module the candidate has a paper in. Fetched
  // here rather than on the exam screen because they are now watched before a
  // section can be started at all.
  useEffect(() => {
    const modules = [...new Set(rows.map((r) => r.test.module))];
    if (modules.length === 0) return;
    let live = true;
    Promise.all(
      modules.map((module) =>
        api
          .get<{ instructions: { part: number | null; url: string; mimeType: string | null }[] }>(
            `/instructions/${module}`,
          )
          .then(({ data }) => {
            const whole = data.instructions.find((i) => i.part === null);
            return whole ? ([module, { url: whole.url, mimeType: whole.mimeType }] as const) : null;
          })
          .catch(() => null),
      ),
    ).then((found) => {
      if (!live) return;
      setFilms(Object.fromEntries(found.filter((f): f is NonNullable<typeof f> => f !== null)));
    });
    return () => {
      live = false;
    };
  }, [rows]);

  const confirm = (assignmentId: string) => {
    setConfirmed((prev) => {
      const next = new Set(prev).add(assignmentId);
      writeConfirmed(next);
      return next;
    });
  };

  const status = (row: AssignmentRow) => {
    if (isDone(row)) return { text: 'Completed', tone: 'done' as const };
    if (row.session?.state === 'IN_PROGRESS') return { text: 'In progress', tone: 'live' as const };
    if (row.window === 'BEFORE') return { text: 'Not open yet', tone: 'wait' as const };
    if (row.window === 'AFTER') return { text: 'Closed', tone: 'wait' as const };
    return { text: 'Not completed', tone: 'todo' as const };
  };

  const when = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  /** "Not open yet" without a date is a reason to go and find the teacher. */
  const availability = (row: AssignmentRow) => {
    if (row.window === 'BEFORE' && row.opensAt) return `Opens ${when(row.opensAt)}`;
    if (row.window === 'AFTER' && row.closesAt) return `Closed ${when(row.closesAt)}`;
    if (row.window === 'OPEN' && row.closesAt && !isDone(row)) {
      return `Sit it before ${when(row.closesAt)}`;
    }
    return null;
  };

  const canSit = (row: AssignmentRow) =>
    row.window === 'OPEN' &&
    !row.blockedBy &&
    (row.session === null || row.session.state === 'IN_PROGRESS');

  /**
   * What is still to sit. A finished section leaves the day: a candidate
   * looking at a fresh set of papers among a dozen ticked-off old ones could
   * not tell which were new, and started the wrong one or none. What they sat
   * is under My results, which is where they go to see it anyway.
   */
  const ahead = rows.filter((row) => !isDone(row));
  const finished = rows.length - ahead.length;
  const allDone = rows.length > 0 && ahead.length === 0;

  // Nothing about the day is shown until the candidate has been through the
  // entry screen, exactly as the sitting they are practising for does it.
  if (!loading && !entered) {
    return (
      <CandidateEntry
        sections={ahead.length}
        finished={finished}
        onEnter={() => {
          try {
            sessionStorage.setItem(ENTERED_KEY, '1');
          } catch {
            // Storage is blocked; the entry screen simply reappears on reload.
          }
          setEntered(true);
        }}
      />
    );
  }

  return (
    <>
      {/* The sitting's name, where the real screen puts the assessment's. The
          candidate's own name and number are in the bar above, so they are not
          repeated here. */}
      <h1 className="candidate__sitting-title">Computer-delivered practice test</h1>

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
              Nothing assigned yet
            </p>
            <p className="muted">Your teacher will assign practice tests here.</p>
          </div>
        ) : (
          <>
            <h2 className="today">Today</h2>

            {/* The check the candidate has already been through, ticked off.
                The real screen opens the day with it. */}
            <section className="sitting sitting--check">
              <div className="sitting__head">
                <h3 className="sitting__name">Pre-test checks</h3>
                <Tick />
              </div>
            </section>

            {ahead.map((row) => {
              const state = status(row);
              const note = availability(row);
              const film = films[row.test.module];
              const done = isDone(row);
              // No film for this module means nothing to confirm; a section is
              // not held shut because an administrator has not uploaded one.
              //
              // A section still waiting its turn shows no film either. The
              // papers are sat in order, and watching the Reading instructions
              // during the Listening is neither the exam nor what the real
              // screen offers: it prints a section not yet reached with its
              // name, its state and nothing to open.
              const hasFilm = Boolean(film) && !done && !row.blockedBy;
              /*
               * A centre that has turned the requirement off still shows the
               * film — it is turning off "compulsory", not "available". So the
               * panel is drawn either way and only the gate below it moves.
               */
              const needsFilm = hasFilm && !skippable;
              const isConfirmed = !needsFilm || confirmed.has(row.id);
              const expanded = open === row.id;

              return (
                <section key={row.id} className="sitting">
                  <div className="sitting__head">
                    <div className="sitting__body">
                      <h3 className="sitting__name">{sectionName(row.test)}</h3>
                      <p className={`sitting__status sitting__status--${state.tone}`}>
                        {state.text}
                      </p>
                      <p className="sitting__timing">{timing(row.test.durationMs)}</p>
                      {note && <p className="sitting__note">{note}</p>}
                    </div>

                    {done && <Tick />}
                  </div>

                  {hasFilm && (
                    <div className="testinfo">
                      <button
                        type="button"
                        className="testinfo__head"
                        aria-expanded={expanded}
                        onClick={() => setOpen(expanded ? null : row.id)}
                      >
                        <span className={`testinfo__chevron ${expanded ? 'is-open' : ''}`}>›</span>
                        <span>Test information.</span>
                        <span
                          className={`testinfo__state ${
                            isConfirmed ? 'testinfo__state--done' : ''
                          }`}
                        >
                          {confirmed.has(row.id)
                            ? 'Confirmed'
                            : needsFilm
                              ? 'Watch before you start'
                              : 'Optional'}
                        </span>
                      </button>

                      {expanded && (
                        <div className="testinfo__body">
                          <InstructionVideo
                            src={film!.url}
                            mimeType={film!.mimeType}
                            onWatched={() => confirm(row.id)}
                            optional={!needsFilm}
                          />
                          {/*
                            * Only where the centre has said it may be skipped.
                            * It closes the panel as watching it through would,
                            * so the screen reads the same either way.
                            */}
                          {!needsFilm && !confirmed.has(row.id) && (
                            <button
                              type="button"
                              className="btn testinfo__skip"
                              onClick={() => {
                                confirm(row.id);
                                setOpen(null);
                              }}
                            >
                              Skip the instructions
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {!done && canSit(row) && (
                    <div className="sitting__go">
                      <button
                        className="btn btn--dark"
                        disabled={!isConfirmed}
                        onClick={() => navigate(`/my/exam/${row.id}`)}
                      >
                        → {row.session?.state === 'IN_PROGRESS' ? 'Resume' : 'Start'}{' '}
                        {sectionName(row.test)}
                      </button>
                      {!isConfirmed && (
                        <p className="field__hint">
                          Open <strong>Test information</strong> above and watch it through. It
                          cannot be skipped.
                        </p>
                      )}
                    </div>
                  )}
                </section>
              );
            })}

            {!allDone && finished > 0 && (
              <p className="muted">
                Tests you have finished are under <Link to="/my/results">My results</Link>.
              </p>
            )}

            {allDone && (
              <div className="sitting__closing">
                <p>
                  Well done. No more tests today — your marks are under{' '}
                  <Link to="/my/results">My results</Link>.
                </p>
                <button
                  className="btn btn--dark"
                  onClick={() => {
                    void signOut().then(() => navigate('/login'));
                  }}
                >
                  Exit
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
